import type { FormEvent, ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import { SearchField } from '../forms/SearchField';
import { cn } from '../ui/utils';

type FilterBarProps = {
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  search?: ReactNode;
  filters?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  className?: string;
  children?: ReactNode;
};

function FilterBar({ onSubmit, search, filters, actions, footer, className, children }: FilterBarProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!onSubmit) {
      event.preventDefault();
      return;
    }

    onSubmit(event);
  };

  const renderSearch = (node: ReactNode) => {
    if (isValidElement(node) && node.type === SearchField) {
      return cloneElement(node as ReactElement<{ filter?: boolean }>, { filter: true });
    }

    return node;
  };

  const formContent = children ?? (
    <>
      {search ? <div className="nx-filters-search">{renderSearch(search)}</div> : null}
      {filters ? <div className="nx-filters-selects">{filters}</div> : null}
      {actions ? (
        <div className="nx-filters-actions">
          <div className="nx-filter-field nx-filter-field--action">
            <span className="nx-filter-field__label-spacer" aria-hidden>
              {'\u00a0'}
            </span>
            <div className="nx-filter-actions-row">{actions}</div>
          </div>
        </div>
      ) : null}
      {footer ? <div className="nx-filters-footer">{footer}</div> : null}
    </>
  );

  return (
    <form className={cn('nx-filters', className)} onSubmit={handleSubmit}>
      {formContent}
    </form>
  );
}

type FilterFieldProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

function FilterField({ label, children, className }: FilterFieldProps) {
  return (
    <label className={cn('nx-filter-field', className)}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export { FilterBar, FilterField };
export type { FilterBarProps, FilterFieldProps };
